import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { TaskFile, TaskFileParams } from '@app/interfaces/lm/task';
import { TicketFile, TicketFilesParams } from '@app/interfaces/lm/ticket';
import { STChange, STColumn, STData, STScroll } from '@app/interfaces/sys/st';
import { FileService } from '@services/lm/file.service';
import { TaskFileService } from '@services/lm/task-file.service';
import { TicketFileService } from '@services/lm/ticket-file.service';
import { SettingsService } from '@services/sys/settings.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html',
  styleUrls: ['./file-list.component.less']
})
export class FileListComponent implements OnInit {
    @Input() projectId!: string;
    @Input() actionDisabled!: boolean;
    @Input() ticketId!: string;
    @Input() taskId!: string;
    @Input() needSearch = true;
    @Output() editLink = new EventEmitter();
    @Input() canEditDelete = true;
    @Output() successRefresh = new EventEmitter();

    columns: STColumn[] = [
        {
            title: 'File Name',
            index: 'name',
            sort: true,
            width: '100px',
        },
        {
            title: 'Category',
            index: 'category',
            format: (item: STData) => {
                return this.judgeIsLink(item.path) ? 'Link' : 'File';
            },
            width: '100px',
        },
        {
            title: 'File Size',
            index: 'size',
            format: (item: STData) => {
                return item.size && !this.judgeIsLink(item.path) ? this.formatSize(item.size) : '';
            },
            sort: true,
            width: '100px',
        },
        {
            title: 'Upload Date',
            index: 'createdDate',
            type: 'date',
            dateFormat: 'YYYY-MM-dd HH:mm:ss',
            sort: true,
            width: '140px',
        },
        {
            title: 'Uploaded By',
            index: 'createdByName',
            // sort: true,
            width: '120px',
        },
        {
            title: 'Actions',
            render: 'action',
            className: 'remove-padding',
            width: '200px',
        },
    ];
    page = 0;
    size = 10;
    total = 0;
    data: Array<TicketFile|TaskFile> = [];
    loading = false;
    params: {
        [key: string]: string
    } = {
        keyword: '',
    };
    nzScroll: STScroll = {};

    constructor(private settingService: SettingsService,
                private fileService: FileService,
                private taskFileService: TaskFileService,
                private ticketFileService: TicketFileService,
                private modal: NzModalService,
                private message: NzMessageService) {
    }

    @HostListener('window:resize')
    _resize(): void {
        this.setScroll();
    }

    setScroll(): void {
        this.settingService.setLayout();
        const { y } = this.settingService.layout;
        this.nzScroll = {
            x: '500px',
            y: `${y}px`,
        };
    }

    ngOnInit(): void {
        this.setScroll();
        this.fetchData();
    }

    judgeIsLink(path: string): boolean {
        const myreg = /(http|https):\/\/([\w.]+\/?)\S*/;
        return myreg.test(path);
    }

    handleChange(event: STChange): void {
        const { type, pi, sort, ps } = event;
        if (type === 'pi') {
            this.page = pi - 1;
            this.fetchData();
        }
        if (type === 'sort') {
            this.params = {
                ...this.params,
                sort,
            };
            this.handleRefresh();
        }

        if (type === 'ps') {
            this.size = ps;
            this.handleRefresh();
        }
    }

    getSearch(keyword: string): void {
        this.params = {
            ...this.params,
            keyword
        };
        this.fetchData();
    }

    handleRefresh(): void {
        this.page = 0;
        this.fetchData();
    }

    refreshEmitter(): void {
        this.handleRefresh();
        this.successRefresh.emit();
    }

    async fetchData(): Promise<void> {
        try {
            console.log('projectId', this.projectId, this.taskId);
            this.loading = true;
            const query: TaskFileParams | TicketFilesParams = {
                page: this.page,
                size: this.size,
                ...this.params,
            };
            if (this.taskId !== undefined) {
                query.taskId = this.taskId;
            }
            if (this.projectId !== undefined) {
                query.projectId = this.projectId;
            }
            if (this.ticketId !== undefined) {
                query.ticketId = this.ticketId;
            }
            const { content, totalElements } = this.ticketId ? await this.ticketFileService.find(query) : await this.taskFileService.find(query);
            this.data = content;
            this.total = totalElements;
        } finally {
            this.loading = false;
        }
    }

    handleDownload(row: TaskFile): void {
        console.log('handleDownload', row);
        this.fileService.download(row.path);
    }

    edit(row: TaskFile): void {
        this.editLink.emit(row);
    }

    view(row: TaskFile): void {
        window.open(row.path);
    }

    formatSize(size: number): string {
        if (size > 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(1) + ' MB';
        } else if (size > 52) {
            return (size / 1024).toFixed(1) + ' KB';
        }
        return size + ' B';
    }

    handleRemove(row: TaskFile): void {
        const isLink = this.judgeIsLink(row.path);
        const title = isLink ? 'link' : 'file';
        this.modal.confirm({
            nzTitle: 'Delete this ' + title + '?',
            nzOkText: 'Yes',
            nzCancelText: 'No',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: { top: '40vh' },
            nzOnOk: async () => {
                this.ticketId ? await this.ticketFileService.remove({ ticketFileId: row.id }) : await this.taskFileService.remove({id: row.id as string});
                if (!isLink) {
                    await this.fileService.remove({path: row.path});
                }
                this.message.success('Deleted successfully');
                this.handleRefresh();
            },
        });
    }

}
